import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { Stage, CURRENT_STAGE } from 'src/contstant/apiEndpoints';

/**
 * Feature flags configuration for the application
 * These flags control the availability of features across the app
 */
export interface FeatureFlags {
  /**
   * Enable editing of translations for non-default languages
   * When true, users can edit completed translations
   * When false, only default language (English) can be edited
   */
  enableTranslationEdit: boolean;
  /**
   * Enable task draft creation for failed audit questions
   * When true, auditors can create/view/update task drafts during perform audit
   * When false, draft-related UI is hidden
   */
  enableAuditTaskDrafts: boolean;
  /**
   * Enable the Divisional Configuration page (NA Reason Codes management)
   * When true, the configuration tab is visible in navigation
   * When false, it is hidden
   */
  enableDivisionalConfiguration: boolean;
  /**
   * Enable reopening completed tasks
   * When true, auditors/admins can reopen completed tasks with a new due date
   * When false, reopen button is hidden
   */
  enableTaskReopen: boolean;
  /**
   * Enable CI project prompt on perfect audit score
   * When true, a modal and inline button encourage sharing best practices via CI portal
   * When false, CI project UI is hidden
   */
  enableCIProjectPrompt: boolean;
  /**
   * Enable managing multiple assignees for an audit
   * When true, the perform audit page shows a multi-assignee modal with add/remove capabilities
   * When false, the legacy single-assignee modal is used
   */
  enableMultiAssignee: boolean;
}

/**
 * Environment-based feature flag configuration
 * Configure flags per stage (test, beta, gamma, prod)
 */
const FEATURE_FLAGS_BY_STAGE: Record<Stage, FeatureFlags> = {
  [Stage.TEST]: {
    enableTranslationEdit: true,
    enableAuditTaskDrafts: true,
    enableDivisionalConfiguration: true,
    enableTaskReopen: true,
    enableCIProjectPrompt: true,
    enableMultiAssignee: true,
  },
  [Stage.BETA]: {
    enableTranslationEdit: true,
    enableAuditTaskDrafts: true,
    enableDivisionalConfiguration: true,
    enableTaskReopen: true,
    enableCIProjectPrompt: true,
    enableMultiAssignee: true,
  },
  [Stage.GAMMA]: {
    enableTranslationEdit: true,
    enableAuditTaskDrafts: true,
    enableDivisionalConfiguration: true,
    enableTaskReopen: true,
    enableCIProjectPrompt: true,
    enableMultiAssignee: true,
  },
  [Stage.PROD]: {
    enableTranslationEdit: true,
    enableAuditTaskDrafts: true,
    enableDivisionalConfiguration: true,
    enableTaskReopen: true,
    enableCIProjectPrompt: true,
    enableMultiAssignee: true,
  },
};

/**
 * Gets feature flags for the current environment
 */
const getFeatureFlagsForEnvironment = (): FeatureFlags => {
  return FEATURE_FLAGS_BY_STAGE[CURRENT_STAGE] ?? FEATURE_FLAGS_BY_STAGE[Stage.BETA];
};

/**
 * React Context for feature flags
 */
const FeatureFlagsContext = createContext<FeatureFlags>(getFeatureFlagsForEnvironment());

/**
 * Hook to access feature flags from any component
 *
 * @returns Current feature flags configuration
 *
 * @example
 * ```tsx
 * const { enableTranslationEdit } = useFeatureFlags();
 *
 * if (enableTranslationEdit) {
 *   // Show translation editing UI
 * }
 * ```
 */
export const useFeatureFlags = (): FeatureFlags => {
  const context = useContext(FeatureFlagsContext);

  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }

  return context;
};

/**
 * Provider component that makes feature flags available to the entire app
 * Wrap your app with this provider at the root level
 *
 * @example
 * ```tsx
 * <FeatureFlagsProvider>
 *   <App />
 * </FeatureFlagsProvider>
 * ```
 */
export const FeatureFlagsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const flags = useMemo(() => getFeatureFlagsForEnvironment(), []);

  return (
    <FeatureFlagsContext.Provider value={flags}>{children}</FeatureFlagsContext.Provider>
  );
};
